import React, { useEffect, useRef } from 'react';

import { IPaymentMethod } from 'features/payment/types';
import { usePickupServiceModeFallback } from 'features/store/hooks/use-pickup-service-mode-fallback';
import { TextInput as RNTextInput } from 'react-native';

import { ServiceModeBar } from 'components/app-header/mobile-header/service-mode-bar';
import { CardModal, CustomScrollViewProps } from 'components/card-modal';
import { CardModalHeader } from 'components/card-modal-header';
import { LogUserErrorSeverity, useLogUserErrorMessage } from 'hooks/log-user-error-message';
import { useCart } from 'hooks/use-cart';
import { useSyncStorageString } from 'hooks/use-local-storage-state/use-sync-storage-state';
import { FutureOrderTimeCard } from 'pages/cart/future-order-time-card';
import { HeaderPoints } from 'pages/cart/header-points';
import { useCartHasUnavailableOffers } from 'pages/cart/hooks/use-cart-has-unavailable-offers';
import YourCart from 'pages/cart/your-cart';
import { DeliveryCancellationPolicy } from 'pages/cart/your-cart/delivery-cancellation-policy';
import { useLoyaltyIncentivesAvailability } from 'pages/loyalty/loyalty-incentives-components/use-loyalty-incentives-availability';
import { useAuthContext } from 'state/auth';
import { useCartContext } from 'state/cart';
import { selectors, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { IncentiveEvaluationErrorCodes } from 'state/loyalty/hooks/types';
import { useServiceModeContext } from 'state/service-mode';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import { logger } from 'utils/logger';

import { Payment } from './payment';

export interface ICartContent {
  checkoutPaymentMethod?: IPaymentMethod | null;
}

const CartContent = ({ checkoutPaymentMethod }: ICartContent) => {
  const { cartEntries, calculateCartTotalWithDiscount } = useCart();
  const { serverOrder, shouldSkipPriceOrder } = useCartContext();
  const { isDelivery } = useServiceModeContext();
  const cartScrollView = useRef<CustomScrollViewProps>(undefined);

  const { user } = useAuthContext();

  const isCartCancelEnabled = useFlag(LaunchDarklyFlag.ENABLE_CART_CANCELLATION_POLICY);

  const phoneNumberInputRef = useRef<RNTextInput>(undefined);

  const filteredOfferErrorList = useAppSelector(state =>
    selectors.loyalty.selectIncentiveErrorsFilteredByCode(
      state,
      IncentiveEvaluationErrorCodes.INVALID_PAYMENT_METHOD
    )
  );
  const rewardErrors = useAppSelector(selectors.loyalty.selectRewardsFeedbackMap);
  const selectedLoyaltyOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);
  const { checkLoyaltyOfferAvailability } = useLoyaltyIncentivesAvailability();
  const isSelectedLoyaltyOfferAvailable =
    !selectedLoyaltyOffer || checkLoyaltyOfferAvailability(selectedLoyaltyOffer);
  const cartHasUnavailableOffers = useCartHasUnavailableOffers();

  const [tempFutureOrderingTime] = useSyncStorageString(SyncStorageKey.TEMP_FUTURE_ORDERING_TIME);

  const { isCartTotalNegative = false } = calculateCartTotalWithDiscount();

  const isSelectedOfferOrCartOffersUnavailable =
    !isSelectedLoyaltyOfferAvailable || cartHasUnavailableOffers;

  const offerIsValid = !isSelectedOfferOrCartOffersUnavailable;

  const isPaymentDisabled =
    !!filteredOfferErrorList.length ||
    !!Object.values(rewardErrors).length ||
    isSelectedOfferOrCartOffersUnavailable ||
    isCartTotalNegative;

  useLogUserErrorMessage({
    message: 'An item in your offer is not available at this restaurant.',
    severity: LogUserErrorSeverity.Recoverable,
    logWhenTrue: isSelectedOfferOrCartOffersUnavailable,
  });

  useLogUserErrorMessage({
    message: 'Your cart does not meet the minimum price required',
    context: {
      serverOrder,
    },
    severity: LogUserErrorSeverity.Recoverable,
    logWhenTrue: isCartTotalNegative,
  });

  // Select next available service mode for pickup if the selected service mode is unavailable
  // This is needed to ensure that the user can still place an order if the selected service mode is unavailable
  // when the checkout v2 is enabled we are doing this in the checkout page but we also need to do this in the cart page
  // to ensure the price order can be triggered and in consequence the user can proceed to the next page which is the checkout page.

  // SPIKE: we need to investigate few things here:
  // https://rbictg.atlassian.net/browse/BKPE-23282
  // 1. Do we want to do this globally instead of in the cart page or checkout page?
  // 2. why the price order mutation is not responding with a failure when service mode is not available?
  // 3. Optional but a plus: is there a way to select and store with the available service mode instead of always using the drive thru service mode by default ?
  usePickupServiceModeFallback();

  // NOTE: Temporary log to help troubleshoot "Offer Unavailable" issue,
  useEffect(() => {
    if (isSelectedOfferOrCartOffersUnavailable) {
      logger.warn({
        cognitoId: user?.cognitoId,
        cartEntries,
        isSelectedLoyaltyOfferAvailable,
        cartHasUnavailableOffers,
        selectedLoyaltyOffer,
        localStorageAppliedOffers: syncStorage.getObject(SyncStorageKey.APPLIED_LOYALTY_OFFERS),
        localStorageAppliedRewards: syncStorage.getObject(SyncStorageKey.APPLIED_LOYALTY_REWARDS),
        message: 'Displaying "An item in your offer is not available at this restaurant."',
      });
    }
  }, [
    cartEntries,
    cartHasUnavailableOffers,
    isSelectedLoyaltyOfferAvailable,
    isSelectedOfferOrCartOffersUnavailable,
    selectedLoyaltyOffer,
    user?.cognitoId,
  ]);

  useEffect(() => {
    if (filteredOfferErrorList.length > 0) {
      logger.warn({
        cognitoId: user?.cognitoId,
        cartEntries,
        isSelectedLoyaltyOfferAvailable,
        cartHasUnavailableOffers,
        selectedLoyaltyOffer,
        localStorageAppliedOffers: syncStorage.getObject(SyncStorageKey.APPLIED_LOYALTY_OFFERS),
        localStorageAppliedRewards: syncStorage.getObject(SyncStorageKey.APPLIED_LOYALTY_REWARDS),
        message: 'There is an Offer with errors in the filteredOfferErrorList array',
      });
    }
  }, [
    cartEntries,
    cartHasUnavailableOffers,
    filteredOfferErrorList.length,
    isSelectedLoyaltyOfferAvailable,
    selectedLoyaltyOffer,
    user?.cognitoId,
  ]);

  return (
    <CardModal
      key="cart"
      _headerOverride={
        <>
          <CardModalHeader
            {...{
              headerText: t('yourOrder'),
              dismissType: 'back',
              invertHeaderColor: true,
              customHeaderRight: <HeaderPoints />,
              isFullScreen: true,
            }}
          />
          <ServiceModeBar hideTopOrderStatusBar isInCart={true} />
        </>
      }
      scrollViewRef={cartScrollView}
      scrollView
      webAnimate={false}
      standardPadding
      invertHeaderColor
      isFullScreen
      footer={
        <Payment
          disabled={isPaymentDisabled}
          serverOrder={serverOrder}
          offerIsValid={offerIsValid}
          scrollViewRef={cartScrollView}
          phoneNumberInputRef={phoneNumberInputRef}
          shouldSkipPriceOrder={shouldSkipPriceOrder}
        />
      }
    >
      {tempFutureOrderingTime && (
        <FutureOrderTimeCard dateString={tempFutureOrderingTime} showIcon />
      )}
      <YourCart checkoutPaymentMethod={checkoutPaymentMethod} />
      {(isCartCancelEnabled || isDelivery) && <DeliveryCancellationPolicy />}
    </CardModal>
  );
};

export default React.memo(CartContent);
